import { Constant } from '../../core/constants/constant';
import * as cryptoJs from 'crypto-js';
import { RegexLib } from '../../core/constants/regex.constants';
import { FileType } from '../../features/insights-cube/insights-cube.enum';

export namespace ISPUtil {
  export function validateEmail(email: string) {
    return email.toLowerCase().match(RegexLib.EMAILREGEX);
  }

  export function isSpecialCharacter(str: string) {
    return RegexLib.SPECIALCHAR.test(str);
  }

  export function isNumeric(num: any) {
    return !isNaN(num);
  }
  export function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  export function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0,
          v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }
  export function secToHHMM(time: number, isHHMMSS = false): string {
    // isHHMMSS is used when HH:MM:SS format is compulsory
    try {
      let result = '';
      if (!time) {
        return isHHMMSS ? '00:00:00' : '00:00';
      }

      let minutes: number | string = Math.floor((time / 60) % 60);
      let seconds: number | string = time % 60;
      let hours: number | string = Math.floor(time / 3600);
      if (hours < 10) {
        hours = '0' + hours;
      }
      if (minutes < 10) {
        minutes = '0' + minutes;
      }
      if (seconds < 10) {
        seconds = '0' + seconds;
      }
      result = (time > 3600 ? `${hours}:` : '') + minutes + ':' + seconds;

      if (hours == '00') {
        return isHHMMSS ? `${hours}:${result}` : result;
      } else {
        return result;
      }
    } catch (err) {
      throw err;
    }
  }

  export function hHMMToSec(timeInHHMM: string, isHHMMSS = false): number {
    let result = 0;
    let tempValue = timeInHHMM.split(':');
    if (isHHMMSS) {
      result += +tempValue[0] * 60 * 60;
      result += +tempValue[1] * 60;
      result += +tempValue[2];
    } else {
      result += +tempValue[0] * 60;
      result += +tempValue[1];
    }
    return +result;
  }

  export function getVideoLengthFromURL(url: string) {
    return new Promise<number>((resolve, reject) => {
      try {
        let video = document.createElement('video');
        video.src = url;
        video.pause();
        let duration = 0;
        // console.log(video);
        video.onloadedmetadata = function () {
          duration = Math.floor(video.duration);
          resolve(duration);
        };
      } catch (err) {
        reject(7200);
      }
    });
  }

  export function debounce(timeout: number) {
    // NOTE: to be used as a decorator @ISPUtil.debounce(5000)
    let timeoutRef: ReturnType<typeof setTimeout>;

    return function (
      target: any,
      propertyKey: string,
      descriptor: PropertyDescriptor
    ) {
      const original = descriptor.value;

      descriptor.value = function (...args: any[]) {
        clearTimeout(timeoutRef);

        timeoutRef = setTimeout(() => {
          original.apply(this, args);
        }, timeout);
      };

      return descriptor;
    };
  }

  export const sortBySequence = (
    array: any,
    sequenceArray: any,
    key: string = ''
  ) => {
    // Create a map to store the indices of each element in the sequenceArray
    let sequenceIndexMap: any = {};
    sequenceArray.forEach((item: any, index: any) => {
      sequenceIndexMap[item] = index;
    });

    // Use a custom comparator function to sort the original array based on the sequenceArray
    array.sort((a: any, b: any) => {
      if (key) {
        a = a[key];
        b = b[key];
      }
      const indexA = sequenceIndexMap[a];
      const indexB = sequenceIndexMap[b];

      if (indexA === undefined && indexB === undefined) {
        // If both elements are not in the sequenceArray, maintain their relative order
        return 0;
      } else if (indexA === undefined) {
        // If only 'a' is not in the sequenceArray, move 'a' to the end
        return 1;
      } else if (indexB === undefined) {
        // If only 'b' is not in the sequenceArray, move 'b' to the end
        return -1;
      } else {
        // Compare the indices to sort based on the sequence
        return indexA - indexB;
      }
    });

    return array;
  };

  export const inchToMeters = (inch: number) => {
    let meters = inch * 0.0254;
    return meters;
  };

  export const pxToCm = (px: number) => {
    return px * 0.026;
  };

  export const groupBy = (list: any[], keyGetter: (item: any) => void) => {
    const map = new Map();
    list.forEach((item: any) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  };

  // chipherText = encrypt("SomeSecretKeyabs","This is a sample plain text to test crypto-js and pycrypto libraries")
  export const encrypt = (secretKey: string, plainText: string) => {
    if (
      secretKey.length != 16 &&
      secretKey.length != 24 &&
      secretKey.length != 32
    ) {
      throw Error('key"s base64 encoded Length should be 16, 24 or 32 only.');
    }
    let _key = cryptoJs.enc.Utf8.parse(secretKey);
    let _iv = cryptoJs.enc.Utf8.parse(secretKey);
    let encrypted = cryptoJs.AES.encrypt(plainText, _key, {
      keySize: 16,
      iv: _iv,
      mode: cryptoJs.mode.ECB,
      padding: cryptoJs.pad.Pkcs7,
    });
    let encryptedSrt = encrypted.toString();
    return encryptedSrt;
  };

  export const decrypt = (secretKey: string, encryptedText: string) => {
    let _key = cryptoJs.enc.Utf8.parse(secretKey);
    let _iv = cryptoJs.enc.Utf8.parse(secretKey);

    let decryptedStr = cryptoJs.AES.decrypt(encryptedText, _key, {
      keySize: 16,
      iv: _iv,
      mode: cryptoJs.mode.ECB,
      padding: cryptoJs.pad.Pkcs7,
    }).toString(cryptoJs.enc.Utf8);
    return decryptedStr;
  };

  export const downloadTextFile = (content: string, filename: string) => {
    const data = content;
    const blob = new Blob([data], { type: 'application/octet-stream' });
    var a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = filename; // Set the file name.
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  };

  export const calculateTimelineSizes = (start: number, end: number, videoDuration: number) => {
    start = (start*100)/videoDuration
    end = (end*100)/videoDuration

    return [
      Math.max(start, 0),
      Math.min(end-start, 100),
      Math.max(100-end, 0)
    ]
  }

  export const getNameFromParticipantId = (id: string, isMeeting: boolean) => {
    if (isMeeting) {
      // lm_org_3_proj_227_webmeet_94_parti_-3
      return 'Participant '+ Math.abs(Number(id.split('_parti_').at(-1)));
    }
    // id = org_3_use_3_sti_839_p1
    return 'Participant ' + id.split('_p').at(-1);
  }

  export function isMacOS() {
    var userAgent = window.navigator.userAgent,
        platform = window.navigator.platform,
        macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K', 'macOS'],
        // windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
        // iosPlatforms = ['iPhone', 'iPad', 'iPod'],
        os = null;
  
    if (macosPlatforms.indexOf(platform) !== -1) {
      return true
    } 
    // else if (iosPlatforms.indexOf(platform) !== -1) {
    //   os = 'iOS';
    // } else if (windowsPlatforms.indexOf(platform) !== -1) {
    //   os = 'Windows';
    // } else if (/Android/.test(userAgent)) {
    //   os = 'Android';
    // } else if (!os && /Linux/.test(platform)) {
    //   os = 'Linux';
    // }
    return false;
  }

  export function getPrecisionValueUpto(num: number, upto: number=2): number{
    return parseFloat((Math.round(num * 100) / 100).toFixed(upto));
  }

  export function sanitizeContent(html: string): string {
  return html.replace(/<(img|video|audio|iframe|object)[^>]*?>.*?<\/\1>|<(img|video|audio|iframe|object)[^>]*?>/gi, '');
  }

  export function showOldAnalysisReport(date: string): boolean {
    if(!date) return false;
    return new Date(date) < new Date(Constant.NEW_REPORT_FEATURE_DEPLOYMENT_DATE);
  }

  export function isAllowedString(value: string): boolean {
  // Same pattern you gave, token‑for‑token
  const pattern = RegexLib.OnlyAllowedCharactersRegex;
  return pattern.test(value);
  }

  export function ensureUtcSuffix(dateStr: string): string {
    return dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  }

 export function isHtml(content: string): boolean {
  const div = document.createElement('div');
  div.innerHTML = content.trim();
  // If the parsed HTML has element nodes, it's HTML, otherwise plain text
  return div.childNodes.length > 0 && Array.from(div.childNodes).some(node => node.nodeType === Node.ELEMENT_NODE);
}

export type ContentType = 'html' | 'markdown' | 'text';

export function detectContentType(input: string): ContentType {
  if (!input || typeof input !== 'string') return 'text';

  const trimmed = input.trim();

  // --- 1) Detect REAL HTML tags only ---
  // Only match valid HTML tag names (div, p, span, h1, img, table, etc.)
  // DOES NOT match <number>, <anything>, <random>, etc.
  const realHtmlTagRegex = /<\/?(?:[a-zA-Z][a-zA-Z0-9]*)(?:\s[^>]*)?>/;

  if (realHtmlTagRegex.test(trimmed)) {
    return 'html';
  }

  // --- 2) Detect Markdown based on common syntax ---
  const markdownRegex =
    /(^#{1,6}\s)|(^[-*+]\s)|(```[\s\S]*?```)|(\*\*[^*]+\*\*)|(_[^_]+_)|(\[[^\]]+\]\([^)]+\))|(^>{1,3}\s)|(^---$)/m;

  if (markdownRegex.test(trimmed)) {
    return 'markdown';
  }

  return 'text';
}

export function getCookie(name: string): string | null {
  const cookieName = name + "=";
  const decoded = decodeURIComponent(document.cookie);
  const cookies = decoded.split("; ");

  for (const cookie of cookies) {
    if (cookie.startsWith(cookieName)) {
      return cookie.substring(cookieName.length);
    }
  }

  return null;
}

export function isSlideArray(input: any): boolean {
  return Array.isArray(input) && input.length > 0 && typeof input[0] === 'object';
}


export const FILE_TYPE_ICON_MAP: Record<string, string> = {

  // [FileType.Directory]: 'folder',

  // [FileType.Pdf]: 'picture_as_pdf',

  // [FileType.Doc]: 'description',
  // [FileType.Docx]: 'description',

  // [FileType.Xls]: 'table_chart',
  // [FileType.Xlsx]: 'table_chart',

  // // [FileType.Ppt]: 'slideshow',
  // [FileType.Pptx]: 'slideshow',

  // [FileType.Csv]: 'grid_on',

  // [FileType.Txt]: 'article',

  // [FileType.Md]: 'notes'

  [FileType.Directory]: 'folder',

  [FileType.Pdf]: 'description',

  [FileType.Doc]: 'description',
  [FileType.Docx]: 'description',

  [FileType.Xls]: 'description',
  [FileType.Xlsx]: 'description',

  // [FileType.Ppt]: 'slideshow',
  [FileType.Pptx]: 'description',

  [FileType.Csv]: 'description',

  [FileType.Txt]: 'description',

  [FileType.Md]: 'description'
};

export function getFileTypeIcon(
  fileType: string
): string {

  return (
    FILE_TYPE_ICON_MAP[fileType] ??
    'insert_drive_file'
  );
}

export function getFolderDepth(path: string|null): number {
  if(path == null) return 0;
  let depth = path
    .split('/')
    .filter(segment => segment.trim().length > 0)
    .length;
  return depth > 0 ? depth-1 : 0;
}

}
